import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Table, PageHeader, Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { masterCatalogSaga } from "./saga";
import * as selectors from "./selectors";
import TestCategoryForm from "../../../../forms/TestCategoryForm";
import CommodityCategoryForm from "../../../../forms/CommodityCategoryForm";
import CommodityForm from "../../../../forms/CommodityForm";
import routes from "../../../../../routes";

const STORAGE_KEY = 'masterCatalogFilters';

const MasterCatalog = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: masterCatalogSaga });
  const dispatch = useDispatch();

  const [testCategoryModalVisible, setTestCategoryModalVisible] = useState(false);
  const [commodityCategoryModalVisible, setCommodityCategoryModalVisible] = useState(false);
  const [commodityModalVisible, setCommodityModalVisible] = useState(false);
  const history = useHistory();
  const location = useLocation();

  // Initialize state from sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }
    
    return {
      page: 1,
      filteredInfo: {
        category: [],
        commodity: [],
        testCategory: [],
      },
    };
  };

  const initialState = getInitialState();
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [filteredInfo, setFilteredInfo] = useState(initialState.filteredInfo);

  const tests = useSelector(selectors.selectTests) || [];
  const categories = useSelector(selectors.selectCommodityCategories) || [];
  const commodities = useSelector(selectors.selectCommodities) || [];
  const testCategories = useSelector(selectors.selectTestCategories) || [];
  const loading = useSelector(selectors.selectLoading);
  const totalTests = useSelector(selectors.selectTestCount);
  const error = useSelector(selectors.selectError);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = {
      page: currentPage,
      filteredInfo,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [currentPage, filteredInfo]);

  // Handle errors
  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Message",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  // Fetch catalog data when filters change
  useEffect(() => {
    let query = `page=${currentPage}&limit=10`;
    if (filteredInfo.category?.length)
      filteredInfo.category.forEach((v) => (query += `&commodityCategory=${v}`));
    if (filteredInfo.commodity?.length)
      filteredInfo.commodity.forEach((v) => (query += `&commodity=${v}`));
    if (filteredInfo.testCategory?.length)
      filteredInfo.testCategory.forEach((v) => (query += `&testCategory=${v}`));

    dispatch(actions.fetchCatalog({ query }));
  }, [dispatch, filteredInfo.category, filteredInfo.commodity, filteredInfo.testCategory, currentPage]);

  // Fetch dropdown options on mount
  useEffect(() => {
    dispatch(actions.fetchCommodityCategories());
    dispatch(actions.fetchCommodity());
    dispatch(actions.fetchTestCategory());
  }, [dispatch]);

  const tableData = tests.map((test) => ({
    category: test?.commodityCategory?.name,
    commodity: test?.commodity?.name,
    parameter: test?.parameter,
    testCategory: test?.testCategory?.name,
    price: `${test?.currency?.value || ""} ${test?.price}`,
    key: test._id,
  }));

  const getCommodityCategoryFilters = () =>
    (categories || [])
      .map((cat) => ({ text: cat.name, value: cat._id }))
      .sort((a, b) => a.text.localeCompare(b.text));

  const getCommodityFilters = () =>
    (commodities || [])
      .map((cat) => ({ text: cat.name, value: cat._id }))
      .sort((a, b) => a.text.localeCompare(b.text));

  const getTestCategoryFilters = () =>
    (testCategories || [])
      .map((cat) => ({ text: cat.name, value: cat._id }))
      .sort((a, b) => a.text.localeCompare(b.text));

  const columns = [
    {
      title: "Category",
      dataIndex: "category",
      filters: getCommodityCategoryFilters(),
      filterMode: "menu",
      filterSearch: true,
      filteredValue: filteredInfo.category || null,
      width: "20%",
    },
    {
      title: "Commodity",
      dataIndex: "commodity",
      filters: getCommodityFilters(),
      filterMode: "menu",
      filterSearch: true,
      filteredValue: filteredInfo.commodity || null,
      width: "15%",
    },
    {
      title: "Test Type",
      dataIndex: "testCategory",
      filters: getTestCategoryFilters(),
      filterMode: "menu",
      filterSearch: true,
      filteredValue: filteredInfo.testCategory || null,
      width: "15%",
    },
    {
      title: "Parameter",
      dataIndex: "parameter",
      width: "35%",
    },
    {
      title: "Price",
      dataIndex: "price",
      width: "15%",
    },
  ];

  const handleSaveTestCategory = (payload) => {
    dispatch(actions.saveTestCategory(payload));
  };

  const handleSaveCommodityCategory = (payload) => {
    dispatch(actions.saveCommodityCategory(payload));
  };

  const handleSaveCommodity = (payload) => {
    dispatch(actions.saveCommodity(payload));
  };

  const handleTableChange = (pagination, filters) => {
    setCurrentPage(pagination.current);
    setFilteredInfo({
      category: filters.category || [],
      commodity: filters.commodity || [],
      testCategory: filters.testCategory || [],
    });
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Master Catalog"
        extra={[
          <Button
            onClick={() => setTestCategoryModalVisible(true)}
            key="1"
            type="secondary"
          >
            Add Test Category
          </Button>,
          <Button
            key="2"
            type="secondary"
            onClick={() => setCommodityCategoryModalVisible(true)}
          >
            Add Commodity Category
          </Button>,
          <Button
            key="3"
            type="secondary"
            onClick={() => setCommodityModalVisible(true)}
          >
            Add Commodity
          </Button>,
          <Button key="4" type="primary">
            <Link to={routes.backOffice.catalog.tests.new}>Add New Test</Link>
          </Button>,
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <Table
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: 10,
            total: totalTests,
            showSizeChanger: false,
            disabled: tableData.length === 0,
          }}
          onChange={handleTableChange}
        />
      </div>
      <TestCategoryForm
        visible={testCategoryModalVisible}
        setVisible={setTestCategoryModalVisible}
        onSave={handleSaveTestCategory}
      />
      <CommodityCategoryForm
        visible={commodityCategoryModalVisible}
        setVisible={setCommodityCategoryModalVisible}
        onSave={handleSaveCommodityCategory}
      />
      <CommodityForm
        visible={commodityModalVisible}
        setVisible={setCommodityModalVisible}
        onSave={handleSaveCommodity}
        categories={categories}
      />
    </div>
  );
};

export default MasterCatalog;